/**
 * SearchSelect — the canonical portal single-select dropdown.
 *
 * Styled replacement for a native <select>: same trigger/panel/option styling
 * as MultiSelect (the Vendor Statements standard), searchable, keyboard
 * accessible (arrow keys + Enter, Escape to close), mobile friendly. Closes on
 * pick. Styling lives in global.css under `.dd-*` / `.dd-option*` — never
 * restyle inline. Use where a plain native <select class="form-select"> is not
 * enough (search, long lists, consistent styling).
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import type { SelectOption } from './MultiSelect';

interface SearchSelectProps {
  options: SelectOption[];
  value: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  searchable?: boolean;
  disabled?: boolean;
  /** Extra class on the trigger, e.g. form-input--error / form-select--attention for validation. */
  className?: string;
  /** 'full' (default) fills its container; 'inline' sits at content width for toolbars/filters. */
  width?: 'full' | 'inline';
  /** Cap how many filtered rows render at once (big lists). Omit = render all. */
  maxVisible?: number;
}

export default function SearchSelect({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  searchable = true,
  disabled = false,
  className,
  width = 'full',
  maxVisible,
}: SearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIdx, setActiveIdx] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  // The panel is positioned fixed to the trigger so it floats above any
  // scrolling/overflow-hidden ancestor (e.g. a modal body) instead of being
  // clipped inside it. Recomputed on open, scroll and resize.
  const [panelStyle, setPanelStyle] = useState<CSSProperties | null>(null);

  const positionPanel = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const PANEL_MAX = 320;
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < Math.min(PANEL_MAX, 240) && r.top > spaceBelow;
    setPanelStyle({
      position: 'fixed',
      left: r.left,
      width: r.width,
      right: 'auto',
      ...(openUp
        ? { bottom: window.innerHeight - r.top + 4, top: 'auto' }
        : { top: r.bottom + 4, bottom: 'auto' }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) { setPanelStyle(null); return; }
    positionPanel();
    const onMove = () => positionPanel();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, positionPanel]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (open && searchable) searchRef.current?.focus();
    if (!open) {
      setSearch('');
      setActiveIdx(-1);
    }
  }, [open, searchable]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || (o.meta ?? '').toLowerCase().includes(q),
    );
  }, [options, search]);

  // Render at most maxVisible rows so a very long list (e.g. every customer)
  // doesn't paint thousands of nodes on open. Searching narrows it.
  const visible = maxVisible ? filtered.slice(0, maxVisible) : filtered;
  const hiddenCount = filtered.length - visible.length;

  function pick(val: string) {
    onChange(val);
    setOpen(false);
  }

  const selectedLabel = options.find((o) => o.value === value)?.label ?? placeholder;

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, visible.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIdx >= 0 && visible[activeIdx]) {
      e.preventDefault();
      pick(visible[activeIdx].value);
    }
  }

  return (
    <div className={`dd ${width === 'inline' ? 'dd--inline' : ''}`} ref={rootRef} onKeyDown={onKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        className={`dd-trigger ${open ? 'dd-open' : ''} ${className ?? ''}`}
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`dd-trigger-label ${value ? '' : 'dd-trigger-label--placeholder'}`}>
          {selectedLabel}
        </span>
        <span className="dd-caret">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="dd-panel" role="listbox" style={panelStyle ?? undefined}>
          {searchable && (
            <div className="dd-panel-head">
              <input
                ref={searchRef}
                type="text"
                className="dd-search"
                placeholder="Search..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setActiveIdx(-1);
                }}
              />
            </div>
          )}
          {filtered.length === 0 ? (
            <div className="dd-empty">No matches.</div>
          ) : (
            <>
              {visible.map((o, idx) => (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={value === o.value}
                  className={`dd-option ${value === o.value ? 'dd-option--checked' : ''} ${idx === activeIdx ? 'dd-option--active' : ''}`}
                  onClick={() => pick(o.value)}
                  onMouseEnter={() => setActiveIdx(idx)}
                >
                  <span className="dd-option-name">{o.label}</span>
                  {o.meta ? <span className="dd-option-meta">{o.meta}</span> : null}
                </button>
              ))}
              {hiddenCount > 0 && (
                <div className="dd-empty">{hiddenCount} more — keep typing to narrow.</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
